import { hangup } from '../../../react/features/base/connection/actions.web';
import { MEDIA_TYPE } from '../../../react/features/base/media/constants';
import { muteLocal } from '../../../react/features/video-menu/actions.any';
import { iAmVisitor } from '../../../react/features/visitors/functions';
import WildixEvents from '../../service/WildixEvents';


import { disconnect } from './actions';
import { WIS_EVENTS } from './constants';
import getCurrentAudioStatus from './helpers/getCurrentAudioStatus';
import getWisSocket from './helpers/getWISConnection';
import getWisHeadsetStatus from './helpers/getWisHeadsetStatus';
import getWisVersion from './helpers/getWisVersion';
import sendWisHeadsetHangup from './helpers/sendWisHeadsetHangup';
import sendWisHeadsetPresence from './helpers/sendWisHeadsetPresence';
import sendWisHeadsetStatus from './helpers/sendWisHeadsetStatus';
import sendWisReady from './helpers/sendWisReady';


export default class WIService {
    _socket = null;

    constructor() {
        document.addEventListener(WildixEvents.CONFERENCE_JOINED, () => {
            if (iAmVisitor(APP.store.getState)) {
                return;
            }

            this.init();

            APP.wildix.eventEmitter.on(WildixEvents.CONFERENCE_DISPOSE, () => {
                // do not set statuses in case of x-bees. The x-bees app wil do it.
                if (!APP.isExternalProvider) {
                    sendWisHeadsetPresence(this._socket, true);
                    sendWisHeadsetHangup(this._socket);
                }

                // close iframe quickly in case of x-bees
                const timeout = APP.isExternalProvider ? 0 : 1000;

                // close socket and destroy iframe in case of x-bees
                // wait a little before closing socket to avoid race condition
                setTimeout(() => {
                    // close socket
                    this.hangupSocket();
                }, timeout);

            });

            APP.wildix.eventEmitter.on(WIS_EVENTS.RECONNECT, this.onReconnectNeeded.bind(this));
            APP.wildix.eventEmitter.on(WIS_EVENTS.MUTE, this.onMuteHeadsetButtonPressed);
            APP.wildix.eventEmitter.on(WIS_EVENTS.UNMUTE, this.onUnmuteHeadsetButtonPressed);
            APP.wildix.eventEmitter.on(WIS_EVENTS.HANGUP, this.onHangupHeadsetButtonPressed);
        });
    }

    async init() {
        this._socket = await getWisSocket();

        if (this._socket) {
            // get WIService version
            getWisVersion(this._socket);

            // get headset status
            getWisHeadsetStatus(this._socket);

            // set current headset status
            const currentHeadsetStatus = getCurrentAudioStatus();

            sendWisHeadsetStatus(this._socket, currentHeadsetStatus);

            // set presence
            sendWisHeadsetPresence(this._socket);

            // send ready status
            sendWisReady(this._socket);
        }
    }

    /**
     * Destroy WebSocket connection
     * @param isReconnect
     */
    hangupSocket(isReconnect = false) {
        // update store
        APP.store.dispatch(disconnect());

        // clean up socket
        if (this._socket) {
            this._socket.close();
            this._socket = null;
        }

        // // close iframe in case of x-bees
        if (APP.isExternalProvider && !isReconnect) {
            APP.xBeesEvents.destroyIframe();
        }
    }

    /**
     * Trying to reconnect to the WebSocket
     *  - Max 10 attempts (it's about 10 minutes)
     *  - with every next attempt the delay increased on {attempt * 10 seconds}
     *
     * @return {Promise<unknown> | Promise.Promise}
     */
    reconnect() {
        return new Promise(resolve => {
            let counter = 0;

            const checker = resolver => {
                if (counter >= 10) {
                    return resolver(false);
                }

                // postpone next iteration up to {counter * 10 seconds}
                setTimeout(async () => {
                    this._socket = await getWisSocket();
                    if (this._socket) {
                        return resolver(true);
                    }

                    checker(resolver);
                }, 10 * 1000 * counter);

                counter++;
            };

            checker(resolve);
        });
    }

    /**
     * Try to reopen WebSocket connection
     * @return {Promise<void>}
     */
    async onReconnectNeeded() {
        // close previous first
        this.hangupSocket(true);

        this.reconnect().then(async result => {
            if (result) {
                await this.init();
            }
        });
    }

    onMuteHeadsetButtonPressed() {
        APP.store.dispatch(muteLocal(true, MEDIA_TYPE.AUDIO));
    }

    onUnmuteHeadsetButtonPressed() {
        APP.store.dispatch(muteLocal(false, MEDIA_TYPE.AUDIO));
    }

    onHangupHeadsetButtonPressed() {
        APP.store.dispatch(hangup());
    }
}
